import { Injectable } from '@angular/core'
import { BrandColorDefinitions, CRBSettingKeys, ColorRole, brandColor } from '../api/models/configuration.model'

export interface TokenUse {
    /** Colour key, as declared in BrandColorDefinitions. */
    token: string
    /** CSS properties this token drives on the matched element, for the chip label. */
    properties: string[]
}

interface SelectorUse {
    selectors: string[]
    property: string
}

/** Selectors that reach inside a shadow root cannot be matched from the document. */
const UNMATCHABLE = /::part|::ng-deep|:host|::slotted/

/** Keeps the search around a selected element bounded, however large the container is. */
const DESCENDANT_LIMIT = 80

const roleOf = (token: string): ColorRole => brandColor(token)?.role ?? 'background'

/**
 * Ionic components take their colour from their own palette inside a shadow root, so there is
 * no rule in the document to match. The palette is fed from these colours by BrandService,
 * which is what lets the element be traced back to something editable.
 */
const IONIC_PALETTE_SOURCE: Record<string, { background: string, foreground: string }> = {
    primary: { background: CRBSettingKeys.CallToActionColor, foreground: 'loginButtonTextColor' },
    secondary: { background: CRBSettingKeys.SecondaryFunctionColor, foreground: CRBSettingKeys.MainTextColor },
    tertiary: { background: CRBSettingKeys.ModuleHeaderColor, foreground: CRBSettingKeys.ModuleHeaderTextColor },
}

/** Ionic tags that render a coloured surface. The rest take no colour of their own. */
const IONIC_COLOURED_TAGS = new Set([
    'ION-FAB-BUTTON', 'ION-BUTTON', 'ION-TOOLBAR', 'ION-CHIP', 'ION-BADGE', 'ION-SEGMENT-BUTTON',
])

@Injectable({ providedIn: 'root' })
export class BrandTokenInspectorService {

    /**
     * Which colour token paints which element is already written down, in the stylesheets.
     * Reading it from there means the hover picker never needs the templates to be annotated,
     * and it cannot fall out of step with the CSS.
     */
    private usesByToken: Map<string, SelectorUse[]>

    /** Element colours the inspector can offer. Theme colours are edited in their own panel. */
    private get editableTokens(): Set<string> {
        return new Set(BrandColorDefinitions.map(d => d.key))
    }

    /** Tokens driving the given element, nearest ancestor first. */
    tokensFor(element: Element): TokenUse[] {
        if (!element) return []

        this.indexStylesheets()

        const byToken = new Map<string, Set<string>>()

        this.usesByToken.forEach((uses, token) => {
            for (const use of uses) {
                if (!use.selectors.some(selector => this.safeMatches(element, selector))) continue

                if (!byToken.has(token)) byToken.set(token, new Set())
                byToken.get(token).add(use.property)
            }
        })

        const found = [...byToken].map(([token, properties]) => ({ token, properties: [...properties] }))

        return found.length ? found : this.ionicPaletteTokens(element)
    }

    /** Fallback for Ionic components with no rule of their own, coloured by their palette. */
    private ionicPaletteTokens(element: Element): TokenUse[] {
        if (!IONIC_COLOURED_TAGS.has(element.tagName)) return []

        const palette = element.getAttribute('color') ?? 'primary'
        const source = IONIC_PALETTE_SOURCE[palette]

        if (!source) return []

        return [
            { token: source.background, properties: ['background'] },
            { token: source.foreground, properties: ['color'] },
        ]
    }

    /** Walks up from the hovered element until something themed is found. */
    closestThemed(element: Element, stopAt: Element = document.body): { element: Element, tokens: TokenUse[] } | null {
        let current = element

        while (current && current !== stopAt) {
            const tokens = this.tokensFor(current)
            if (tokens.length) return { element: current, tokens }
            current = current.parentElement
        }

        return null
    }

    /**
     * Colours to offer for a selected element, which is more than the ones its own selectors
     * carry. A surface is usually styled on the container while its text colour is set on the
     * children, or inherited from an ancestor, so every role gets looked up around the element
     * as well. Without this, selecting a toolbar would only ever offer its background.
     */
    tokensForSelection(element: Element): TokenUse[] {
        const own = this.tokensFor(element)
        const covered = new Set(own.map(use => roleOf(use.token)))
        const found = [...own]

        for (const role of ['foreground', 'border'] as ColorRole[]) {
            if (covered.has(role)) continue

            const nearby = this.nearestWithRole(element, role)
            if (nearby) found.push(nearby)
        }

        return found
    }

    private nearestWithRole(element: Element, role: ColorRole): TokenUse {
        // Children first: that is where a container's text colour normally lives.
        const queue: Element[] = Array.from(element.children)
        let visited = 0

        while (queue.length && visited < DESCENDANT_LIMIT) {
            const candidate = queue.shift()
            visited++

            const hit = this.tokensFor(candidate).find(use => roleOf(use.token) === role)
            if (hit) return hit

            queue.push(...Array.from(candidate.children))
        }

        // Then ancestors, because text colour inherits.
        let parent = element.parentElement

        while (parent && parent !== document.body) {
            const hit = this.tokensFor(parent).find(use => roleOf(use.token) === role)
            if (hit) return hit

            parent = parent.parentElement
        }

        return null
    }

    private indexStylesheets(): void {
        if (this.usesByToken) return

        this.usesByToken = new Map()
        const editable = this.editableTokens

        for (let s = 0; s < document.styleSheets.length; s++) {
            let rules: CSSRuleList
            try { rules = document.styleSheets[s].cssRules } catch { continue }
            this.indexRules(rules, editable)
        }
    }

    private indexRules(rules: CSSRuleList, editable: Set<string>): void {
        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i] as CSSStyleRule & { cssRules?: CSSRuleList }

            if (rule.cssRules?.length) {
                this.indexRules(rule.cssRules, editable)
                continue
            }

            if (!rule.selectorText || !rule.style || UNMATCHABLE.test(rule.selectorText)) continue

            this.indexDeclarations(rule, editable)
        }
    }

    private indexDeclarations(rule: CSSStyleRule, editable: Set<string>): void {
        const selectors = rule.selectorText.split(',').map(s => s.trim())

        for (let p = 0; p < rule.style.length; p++) {
            const property = rule.style[p]

            for (const token of this.tokensIn(rule.style.getPropertyValue(property))) {
                if (!editable.has(token)) continue

                if (!this.usesByToken.has(token)) this.usesByToken.set(token, [])
                this.usesByToken.get(token).push({ selectors, property })
            }
        }
    }

    private tokensIn(value: string): string[] {
        const matches = value.match(/var\(\s*--([A-Za-z][A-Za-z0-9]*)/g)

        return matches ? matches.map(m => m.replace(/var\(\s*--/, '')) : []
    }

    private safeMatches(element: Element, selector: string): boolean {
        try { return element.matches(selector) } catch { return false }
    }
}
